import {
    Box,
    Collapse,
    FilledInput,
    FormControl,
    FormHelperText,
    Grow,
    InputLabel,
    MenuItem,
    Select,
  } from '@mui/material';
  import { useCallback, useEffect, useRef, useState } from 'react';
  import { FormField } from '../DyanmicForm';
  
  interface Props {
    form_field: FormField;
    validateField: (
      fieldName: string,
      isValid: boolean,
      error: string,
      fieldValue: any
    ) => void;
    isChild: boolean;
    // if you're programmatically tagging the first child for focus:
    [dataAttr: string]: any;
  }
  
  /* ---------- helpers ---------- */
  const isEmpty = (v: any) =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);
  
  const hasAllowedValues = (field: FormField) =>
    (field.AllowedValues?.length ?? 0) > 0;
  
  /* ---------- component ---------- */
  function DynamicFormField({ form_field, validateField, isChild, ...rest }: Props) {
    const [value, setValue] = useState<any>('');
    const [error, setError] = useState('');
    const [isValid, setIsValid] = useState(!form_field.Required);
    const [touched, setTouched] = useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  
    /* memoised validation fn */
    const runValidation = useCallback(
      (nextVal: any) => {
        let ok = true;
        let err = '';
  
        // Required & empty → red highlight, but no text
        if (form_field.Required && isEmpty(nextVal)) {
          ok = false;
          // *** We deliberately do NOT set err = 'This field is required.' any more
        }
  
        // If not empty, check allowed values
        if (ok && hasAllowedValues(form_field)) {
          if (!form_field.AllowedValues!.includes(nextVal)) {
            ok = false;
            if (touched) err = 'Value not allowed.';
          }
        }
  
        // If still ok, check any custom validation (regex, etc.)
        if (ok && form_field.Validation) {
          const regex = new RegExp(form_field.Validation);
          if (!regex.test(String(nextVal))) {
            ok = false;
            if (touched) err = 'Does not match required format.';
          }
        }
  
        setIsValid(ok);
        setError(err);
        validateField(form_field.Label, ok, err, nextVal);
      },
      [form_field, touched, validateField]
    );
  
    /* re-validate on value change */
    useEffect(() => {
      runValidation(value);
    }, [value, runValidation]);
  
    /* initial registration (once) */
    useEffect(() => {
      // If it's required, we mark it invalid until the user enters something
      // but we won't show a textual error message unless/until touched
      validateField(
        form_field.Label,
        !form_field.Required, // if required, start invalid; if optional, start valid
        '',
        ''
      );
      // We only run once, so skip dependencies
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    /* ---------- UI state ---------- */
    const childrenVisible =
      form_field.Children?.length > 0 &&
      value === form_field.ChildrenShowOnValue;
  
    const handleInput = (e: any) => {
      if (!touched) setTouched(true);
      setValue(e.target.value);
    };
  
    const handleBlur = () => {
      if (!touched) setTouched(true);
      runValidation(value);
    };
  
    // autofocus first newly-shown child
    useEffect(() => {
      if (childrenVisible && form_field.Children?.length) {
        const id = setTimeout(() => {
          const el = document.querySelector(
            `[data-first-child-of="${form_field.FieldId}"]`
          ) as HTMLElement | null;
          el?.focus();
        }, 200);
        return () => clearTimeout(id);
      }
    }, [childrenVisible, form_field]);
  
    const fieldId = `field-${form_field.FieldId}`;
    const helperId = `${fieldId}-hint`;
    const errorId = `${fieldId}-err`;
    const showError = !isValid && touched;
  
    /* ---------- render ---------- */
    return (
      <Box sx={{ pl: isChild ? 2 : 0 }}>
        <Grow in>
          <FormControl
            variant="filled"
            fullWidth
            error={showError}
            {...rest}
          >
            <InputLabel
              htmlFor={fieldId}
              required={form_field.Required}
              id={`${fieldId}-label`}
            >
              {form_field.Label}
            </InputLabel>
  
            {!hasAllowedValues(form_field) ? (
              <FilledInput
                id={fieldId}
                inputRef={inputRef}
                aria-describedby={`${helperId}${showError ? ` ${errorId}` : ''}`}
                aria-invalid={showError || undefined}
                aria-required={form_field.Required || undefined}
                type={form_field.DataType}
                disableUnderline
                value={value}
                onChange={handleInput}
                onBlur={handleBlur}
              />
            ) : (
              <Select
                labelId={`${fieldId}-label`}
                id={fieldId}
                inputRef={inputRef}
                aria-describedby={`${helperId}${showError ? ` ${errorId}` : ''}`}
                aria-invalid={showError || undefined}
                aria-required={form_field.Required || undefined}
                value={value}
                onChange={handleInput}
                onBlur={handleBlur}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {form_field.AllowedValues!.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            )}
  
            {/* Main description (always shown) */}
            {form_field.Description && (
              <FormHelperText id={helperId}>
                {form_field.Description}
              </FormHelperText>
            )}
  
            {/* Error text only if we actually have an error message */}
            {showError && error && (
              <FormHelperText id={errorId}>{error}</FormHelperText>
            )}
          </FormControl>
        </Grow>
  
        {/* children */}
        {form_field.Children?.length > 0 && (
          <Collapse in={childrenVisible} timeout="auto" unmountOnExit>
            <Box
              role="group"
              aria-labelledby={`${fieldId}-label`}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
            >
              {form_field.Children.map((child, idx) => (
                <DynamicFormField
                  key={child.FieldId}
                  form_field={child}
                  validateField={validateField}
                  isChild
                  data-first-child-of={idx === 0 ? form_field.FieldId : undefined}
                />
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  }
  
  export default DynamicFormField;
  